'use client';

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Drawer,
  Rate,
  DatePicker,
  Input,
  Upload,
  Button,
  Form,
  Collapse,
  message,
} from 'antd';
import {
  StarFilled,
  CameraOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import type { UploadFile, RcFile } from 'antd/es/upload';
import dayjs, { type Dayjs } from 'dayjs';
import { FcGoogle } from 'react-icons/fc';
import { FaFacebook, FaShopify } from 'react-icons/fa';

import { useReviewerAuth } from '../contexts/ReviewerAuthContext';
import {
  savePendingReview,
  getPendingReview,
  clearPendingReview,
  submitReview,
  getReviewImageUploadUrl,
  registerReviewImage,
  authenticateAnonymous,
  setReviewerToken,
  type ReviewerAuthProvider,
} from '../utils/reviewerApi';
import { resizeImage } from '../utils/imageResize';
import type { ReviewFormData } from '../types';

// ── Types ────────────────────────────────────────────────────

interface ReviewFormProps {
  entityId: number;
  entityName: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: (review: any) => void;
}

interface SubRatingConfig {
  key: keyof ReviewFormData;
  label: string;
}

// ── Constants ────────────────────────────────────────────────

const SUB_RATINGS: SubRatingConfig[] = [
  { key: 'facilities_rating', label: 'Facilities' },
  { key: 'cleanliness_rating', label: 'Cleanliness' },
  { key: 'value_rating', label: 'Value' },
  { key: 'location_rating', label: 'Location' },
  { key: 'accessibility_rating', label: 'Accessibility' },
  { key: 'safety_rating', label: 'Safety' },
  { key: 'pet_friendliness_rating', label: 'Pet-friendliness' },
  { key: 'family_friendliness_rating', label: 'Family-friendliness' },
];

const MAX_PHOTOS = 5;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

const OVERALL_RATING_LABELS: Record<number, string> = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Very Good',
  5: 'Excellent',
};

// ── Component ────────────────────────────────────────────────

const ReviewForm: React.FC<ReviewFormProps> = ({
  entityId,
  entityName,
  isOpen,
  onClose,
  onSubmitSuccess,
}) => {
  const [form] = Form.useForm();
  const { isLoggedIn, login } = useReviewerAuth();

  // Local UI state
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<ReviewerAuthProvider | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [overallRating, setOverallRating] = useState<number>(0);
  const [contentLength, setContentLength] = useState(0);

  // ── Reset form when drawer opens/closes ────────────────────

  useEffect(() => {
    if (!isOpen) return;
    setShowLoginPrompt(false);
    setSubmitting(false);
    setOauthLoading(null);
    // Photos can't survive the OAuth redirect (Files aren't serialisable), so
    // the picker always starts empty.
    setFileList([]);

    // If a draft was stashed before an OAuth redirect (or a failed
    // auto-submit), restore it so the user never has to retype their review.
    const pending = getPendingReview();
    const fd =
      pending && pending.entityId === entityId
        ? (pending.formData as Record<string, any>)
        : null;

    if (fd) {
      const rating = Number(fd.overall_rating) || 0;
      setOverallRating(rating);
      setContentLength(typeof fd.content === 'string' ? fd.content.length : 0);
      form.setFieldsValue({
        ...fd,
        // formData persists visit_date as 'YYYY-MM'; DatePicker needs a Dayjs.
        visit_date: fd.visit_date ? dayjs(`${fd.visit_date}-01`) : undefined,
      });
    } else {
      form.resetFields();
      setOverallRating(0);
      setContentLength(0);
    }
  }, [isOpen, form, entityId]);

  // ── File handling ──────────────────────────────────────────

  const beforeUpload = useCallback((file: RcFile): boolean | string => {
    const ext = file.name?.split('.').pop()?.toLowerCase() || '';
    const isHeic = ext === 'heic' || ext === 'heif';
    const isAcceptedType = ACCEPTED_IMAGE_TYPES.includes(file.type) || isHeic;
    if (!isAcceptedType) {
      message.error('Only JPG, PNG, WebP, and HEIC images are allowed.');
      return Upload.LIST_IGNORE;
    }
    const isUnderLimit = file.size <= MAX_FILE_SIZE_BYTES;
    if (!isUnderLimit) {
      message.error(`File must be smaller than ${MAX_FILE_SIZE_MB}MB.`);
      return Upload.LIST_IGNORE;
    }
    return false; // Prevent auto-upload; store locally
  }, []);

  const handleFileChange = useCallback(
    ({ fileList: newList }: { fileList: UploadFile[] }) => {
      setFileList(newList.slice(0, MAX_PHOTOS));
    },
    [],
  );

  const handleRemoveFile = useCallback(
    (file: UploadFile) => {
      setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
    },
    [],
  );

  // ── Upload photos to S3 after review is created ────────────

  const uploadPhotos = useCallback(
    async (reviewId: string) => {
      const photoFiles = fileList
        .map((f) => f.originFileObj)
        .filter(Boolean) as File[];

      if (photoFiles.length === 0) return;

      const uploadPromises = photoFiles.map(async (file, idx) => {
        try {
          // Resize and convert HEIC→JPEG if needed
          const processed = await resizeImage(file);
          const { url, key, cdn_url } = await getReviewImageUploadUrl(
            reviewId,
            processed.name,
            processed.type,
          );
          // Upload to S3
          await fetch(url, {
            method: 'PUT',
            body: processed,
            headers: { 'Content-Type': processed.type },
          });
          // Register image in database
          const imageSrc = cdn_url || key;
          await registerReviewImage(reviewId, {
            image_src: imageSrc,
            sort_order: idx,
            content_type: processed.type,
            file_size: processed.size,
          });
          return key;
        } catch (err) {
          console.error('[ReviewForm] Photo upload failed:', err);
          return null;
        }
      });

      const results = await Promise.allSettled(uploadPromises);
      const failedCount = results.filter(
        (r) => r.status === 'rejected' || (r.status === 'fulfilled' && r.value === null),
      ).length;

      if (failedCount > 0) {
        message.warning(
          `${failedCount} photo(s) failed to upload. Your review was saved without them.`,
        );
      }
    },
    [fileList],
  );

  // ── Build payload from form values ─────────────────────────

  const buildPayload = useCallback(
    (values: any): Record<string, unknown> => {
      const payload: Record<string, unknown> = {
        overall_rating: values.overall_rating,
        title: values.title.trim(),
        content: values.content.trim(),
        visit_date: (values.visit_date as Dayjs).format('YYYY-MM'),
      };

      SUB_RATINGS.forEach(({ key }) => {
        if (values[key]) {
          payload[key as string] = values[key];
        }
      });

      return payload;
    },
    [],
  );

  // ── Submit flow ────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();

      if (isLoggedIn) {
        // User is authenticated -- submit directly
        setSubmitting(true);
        try {
          const payload = buildPayload(values);
          const result = await submitReview(entityId, payload);

          // Upload photos if we got a review ID back
          const reviewId = result?.review?.id || result?.id;
          if (reviewId && fileList.length > 0) {
            await uploadPhotos(reviewId);
          }

          // Drop any draft stashed from an earlier logged-out attempt so it
          // can't re-hydrate a stale review next time the drawer opens.
          clearPendingReview();
          message.success(
            "Thanks! Your review will appear once approved by our team.",
          );
          onSubmitSuccess?.(result);
          onClose();
        } catch (err: any) {
          console.error('[ReviewForm] Submit error:', err);
          message.error(err.message || 'Failed to submit review. Please try again.');
        } finally {
          setSubmitting(false);
        }
      } else {
        // User is NOT authenticated -- save form and prompt login
        const payload = buildPayload(values);
        savePendingReview({
          entityId,
          entityName,
          formData: payload,
        });
        setShowLoginPrompt(true);
      }
    } catch {
      // Validation failed -- Ant Design shows inline errors automatically
    }
  }, [
    form,
    isLoggedIn,
    entityId,
    entityName,
    buildPayload,
    uploadPhotos,
    fileList.length,
    onSubmitSuccess,
    onClose,
  ]);

  // ── OAuth button handler ───────────────────────────────────

  const handleOAuthLogin = useCallback(
    async (provider: ReviewerAuthProvider) => {
      setOauthLoading(provider);
      try {
        await login(provider);
        // login() will redirect the browser; no further code runs here
      } catch (err: any) {
        console.error('[ReviewForm] OAuth error:', err);
        message.error(err.message || 'Sign-in failed. Please try again.');
        setOauthLoading(null);
      }
    },
    [login],
  );

  // ── Anonymous submit handler ──────────────────────────────

  const handleAnonymousSubmit = useCallback(async () => {
    setOauthLoading('google'); // Reuse loading state to disable buttons
    try {
      // Get an anonymous JWT token
      const { token } = await authenticateAnonymous();
      setReviewerToken(token);

      // Build and submit the review
      const values = await form.validateFields();
      const payload = buildPayload(values);
      const result = await submitReview(entityId, payload);

      // Upload photos if any
      const reviewId = result?.review?.id || result?.id;
      if (reviewId && fileList.length > 0) {
        await uploadPhotos(reviewId);
      }

      clearPendingReview();
      message.success(
        "Thanks! Your anonymous review will appear once approved by our team.",
      );
      setShowLoginPrompt(false);
      onSubmitSuccess?.(result);
      onClose();
    } catch (err: any) {
      console.error('[ReviewForm] Anonymous submit error:', err);
      message.error(err.message || 'Failed to submit review. Please try again.');
    } finally {
      setOauthLoading(null);
    }
  }, [form, buildPayload, entityId, fileList.length, uploadPhotos, onSubmitSuccess, onClose]);

  // ── Memoised drawer width (responsive) ─────────────────────

  const drawerWidth = useMemo(() => {
    if (typeof window === 'undefined') return 600;
    return window.innerWidth < 640 ? '100%' : 600;
  }, []);

  // ── Render: photo upload button ────────────────────────────

  const uploadButton = (
    <div className="flex flex-col items-center justify-center gap-1">
      <CameraOutlined className="text-xl text-gray-400" />
      <span className="text-xs text-gray-500">Add Photo</span>
    </div>
  );

  // ── Render: login overlay ──────────────────────────────────

  const loginOverlay = showLoginPrompt ? (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 rounded-lg">
      <div className="bg-white rounded-xl shadow-2xl p-8 mx-4 max-w-sm w-full text-center">
        <button
          onClick={() => setShowLoginPrompt(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Back to editing"
        >
          <CloseOutlined />
        </button>

        <div className="mb-2">
          <StarFilled className="text-4xl text-yellow-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Almost there!
        </h3>
        <p className="text-gray-600 mb-6">
          Sign in to publish your review
        </p>

        <div className="flex flex-col gap-3">
          {/* Google */}
          <Button
            block
            size="large"
            icon={<FcGoogle className="text-xl" />}
            loading={oauthLoading === 'google'}
            disabled={!!oauthLoading}
            onClick={() => handleOAuthLogin('google')}
            className="flex items-center justify-center gap-2 border-gray-300 hover:border-accent h-12"
          >
            Continue with Google
          </Button>

          {/* Facebook */}
          <Button
            block
            size="large"
            icon={<FaFacebook className="text-xl text-[#1877F2]" />}
            loading={oauthLoading === 'facebook'}
            disabled={!!oauthLoading}
            onClick={() => handleOAuthLogin('facebook')}
            className="flex items-center justify-center gap-2 border-gray-300 hover:border-[#1877F2] h-12"
          >
            Continue with Facebook
          </Button>

          {/* Shopify */}
          <Button
            block
            size="large"
            icon={<FaShopify className="text-xl text-[#96BF48]" />}
            loading={oauthLoading === 'shopify'}
            disabled={!!oauthLoading}
            onClick={() => handleOAuthLogin('shopify')}
            className="flex items-center justify-center gap-2 border-gray-300 hover:border-[#96BF48] h-12"
          >
            Continue with Shopify
          </Button>
        </div>

        <p className="text-xs text-gray-400 mt-4">
          We only use your name and email. No spam, ever.
        </p>

        {/* Divider */}
        <div className="flex items-center gap-3 mt-4 mb-3">
          <div className="flex-1 border-t border-gray-200" />
          <span className="text-xs text-gray-400">or</span>
          <div className="flex-1 border-t border-gray-200" />
        </div>

        {/* Anonymous option */}
        <Button
          block
          size="large"
          disabled={!!oauthLoading}
          onClick={handleAnonymousSubmit}
          className="flex items-center justify-center gap-2 border-gray-300 hover:border-gray-500 h-12 text-gray-600"
        >
          Post Anonymously
        </Button>
        <p className="text-[11px] text-gray-400 mt-1">
          Your review will still be moderated before publishing.
        </p>

        <Button
          type="link"
          size="small"
          onClick={() => setShowLoginPrompt(false)}
          className="mt-2 text-gray-500"
        >
          Cancel &mdash; back to editing
        </Button>
      </div>
    </div>
  ) : null;

  // ── Render: main form ──────────────────────────────────────

  return (
    <Drawer
      title={`Review ${entityName}`}
      placement="right"
      width={drawerWidth}
      open={isOpen}
      onClose={onClose}
      destroyOnClose
      className="review-form-drawer"
      styles={{
        body: { paddingBottom: 80 },
      }}
      footer={
        <div className="flex justify-end gap-3 py-2">
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={submitting}
            size="large"
            className="px-8"
          >
            Submit Review
          </Button>
        </div>
      }
    >
      <div className="relative">
        {loginOverlay}

        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          className="review-form"
        >
          {/* ── Overall Rating ────────────────────────────────── */}
          <Form.Item
            name="overall_rating"
            label={
              <span className="text-base font-semibold">
                Overall Rating <span className="text-red-500">*</span>
              </span>
            }
            rules={[
              {
                required: true,
                message: 'Please select an overall rating',
              },
              {
                type: 'number',
                min: 1,
                message: 'Please select an overall rating',
              },
            ]}
          >
            <div className="flex items-center gap-3">
              <Rate
                value={overallRating}
                allowClear
                onChange={(val) => {
                  setOverallRating(val);
                  form.setFieldsValue({ overall_rating: val });
                }}
                className="text-3xl sm:text-4xl"
                style={{ fontSize: 32 }}
              />
              {overallRating > 0 && (
                <>
                  <span className="text-sm text-gray-500 font-medium">
                    {OVERALL_RATING_LABELS[overallRating]}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setOverallRating(0);
                      form.setFieldsValue({ overall_rating: undefined });
                    }}
                    className="text-xs text-gray-400 hover:text-gray-600 underline"
                  >
                    Clear
                  </button>
                </>
              )}
            </div>
          </Form.Item>

          {/* ── Sub-ratings (expandable) ──────────────────────── */}
          <Collapse
            ghost
            className="mb-4 -mx-2"
            items={[
              {
                key: 'sub-ratings',
                label: (
                  <span className="text-sm font-medium text-green-700">
                    Rate specific categories
                  </span>
                ),
                children: (
                  <div className="pt-1">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                      {SUB_RATINGS.map(({ key, label }) => (
                        <Form.Item
                          key={key}
                          name={key}
                          label={
                            <span className="text-sm text-gray-600">
                              {label}
                            </span>
                          }
                          className="mb-0"
                        >
                          <Rate allowHalf allowClear style={{ fontSize: 18 }} />
                        </Form.Item>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const resetFields: Record<string, undefined> = {};
                        SUB_RATINGS.forEach(({ key }) => { resetFields[key] = undefined; });
                        form.setFieldsValue(resetFields);
                      }}
                      className="mt-2 text-xs text-gray-400 hover:text-gray-600 underline"
                    >
                      Clear all categories
                    </button>
                  </div>
                ),
              },
            ]}
          />

          {/* ── Visit Date ────────────────────────────────────── */}
          <Form.Item
            name="visit_date"
            label={
              <span className="text-sm font-semibold">
                When did you visit? <span className="text-red-500">*</span>
              </span>
            }
            rules={[
              {
                required: true,
                message: 'Please select when you visited',
              },
            ]}
          >
            <DatePicker
              picker="month"
              className="w-full"
              placeholder="Select month"
              disabledDate={(current) =>
                current && current.isAfter(new Date())
              }
              popupClassName="review-datepicker-popup"
              onOpenChange={(open) => {
                // Ant Design's popup positioning breaks inside
                // position:fixed Drawers. Manually fix position.
                if (open) {
                  requestAnimationFrame(() => {
                    const popup = document.querySelector(
                      '.review-datepicker-popup',
                    ) as HTMLElement;
                    const picker = document.querySelector(
                      '.review-form-drawer .ant-picker',
                    ) as HTMLElement;
                    if (popup && picker) {
                      const rect = picker.getBoundingClientRect();
                      popup.style.setProperty('position', 'fixed', 'important');
                      popup.style.setProperty(
                        'top',
                        `${rect.bottom + 4}px`,
                        'important',
                      );
                      popup.style.setProperty(
                        'left',
                        `${rect.left}px`,
                        'important',
                      );
                      popup.style.setProperty('inset', 'unset', 'important');
                    }
                  });
                }
              }}
            />
          </Form.Item>

          {/* ── Title ─────────────────────────────────────────── */}
          <Form.Item
            name="title"
            label={
              <span className="text-sm font-semibold">
                Title <span className="text-red-500">*</span>
              </span>
            }
            rules={[
              { required: true, message: 'Please add a title' },
              {
                max: 100,
                message: 'Title must be 100 characters or fewer',
              },
            ]}
          >
            <Input
              placeholder="Summarize your experience"
              maxLength={100}
              showCount
            />
          </Form.Item>

          {/* ── Content ───────────────────────────────────────── */}
          <Form.Item
            name="content"
            label={
              <span className="text-sm font-semibold">
                Your Review <span className="text-red-500">*</span>
              </span>
            }
            rules={[
              { required: true, message: 'Please write a review' },
              {
                min: 50,
                message: 'Please write at least 50 characters',
              },
              {
                max: 2000,
                message: 'Review must be 2000 characters or fewer',
              },
            ]}
          >
            <Input.TextArea
              placeholder="Tell other campers about your experience..."
              rows={5}
              maxLength={2000}
              showCount
              onChange={(e) => setContentLength(e.target.value.length)}
            />
          </Form.Item>
          {contentLength > 0 && contentLength < 50 && (
            <p className="text-xs text-gray-400 -mt-4 mb-4">
              {50 - contentLength} more character{50 - contentLength !== 1 ? 's' : ''} needed
            </p>
          )}

          {/* ── Photo Upload ──────────────────────────────────── */}
          <div className="mb-6">
            <label className="text-sm font-semibold block mb-2">
              Photos{' '}
              <span className="font-normal text-gray-400">
                (optional, up to {MAX_PHOTOS})
              </span>
            </label>
            <Upload
              listType="picture-card"
              fileList={fileList}
              beforeUpload={beforeUpload}
              onChange={handleFileChange}
              onRemove={handleRemoveFile}
              accept=".jpg,.jpeg,.png,.webp,.heic,.heif"
              multiple
              maxCount={MAX_PHOTOS}
              previewFile={(file) =>
                Promise.resolve(URL.createObjectURL(file as unknown as Blob))
              }
            >
              {fileList.length >= MAX_PHOTOS ? null : uploadButton}
            </Upload>
            <p className="text-xs text-gray-400 mt-1">
              JPG, PNG, WebP, or HEIC. Max {MAX_FILE_SIZE_MB}MB each.
            </p>
          </div>
        </Form>
      </div>
    </Drawer>
  );
};

export default ReviewForm;
